import { forwardRef } from 'react';

const formatWeight = (g, weightType) => {
  if (weightType === 'pieces' || weightType === 'count') {
    const val = g % 1 === 0 ? g : g.toFixed(1);
    return `${val} pcs`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)}kg`;
  return `${Math.round(g)}g`;
};

export const PackingPrintPreview = forwardRef(({ packingData, isLinkedAway, getLinkedExtra }, ref) => {
  if (!packingData) return null;

  return (
    <div style={{ position: 'absolute', left: '-9999px', top: 0 }} ref={ref}>
      {packingData.recipe_sheets
        .filter(s => s.category !== 'Add-on')
        .map((sheet) => {
          const today = new Date().toLocaleDateString('en-GB');
          const dayLabel = packingData.selected_days.join(' + ');
          return (
            <div className="page" key={sheet.recipe_id}>
              <div className="header">
                <span className="title">PACKING SHEET</span>
                <span className="font-bold">{dayLabel}</span>
                <span>{today}</span>
                <span>PACKED BY: ______________</span>
              </div>
              <h2>{sheet.recipe_name}</h2>
              {sheet.stickers?.length > 0 && (
                <div data-testid={`print-stickers-${sheet.recipe_id}`} style={{ background: '#6D28D9', color: '#fff', fontWeight: 700, padding: '4px 8px', margin: '4px 0 8px', fontSize: '12px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                  STICKERS: {sheet.stickers.join(' · ')}
                </div>
              )}
              <h3>Component Weights</h3>
              <table>
                <thead>
                  <tr>
                    <th>Component</th>
                    {sheet.size_names.map(sn => <th key={sn} className="text-center">{sn}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(sheet.component_weights).map(([name, info]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      {sheet.size_names.map(sn => (
                        <td key={sn} className="text-center">{info.sizes[sn] !== undefined ? info.sizes[sn] : '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {sheet.individual_clients.length > 0 && (
                <>
                  <h3>Complete Meals</h3>
                  <table>
                    <thead><tr><th>Client</th><th>Key</th><th>Size</th><th className="text-center">Qty</th></tr></thead>
                    <tbody>
                      {sheet.individual_clients.map((c) => (
                        <tr key={c.client_name}>
                          <td>{c.client_name}</td>
                          <td className="font-bold">{c.key}</td>
                          <td>{c.meal_size}</td>
                          <td className="text-center font-bold">{c.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {sheet.sharing_clients.length > 0 && (
                <>
                  <h3>Sharing Packs</h3>
                  <table>
                    <thead><tr><th>Component</th><th>Client</th><th>Key</th><th>Size</th><th className="text-center">Total</th><th>Packaging</th></tr></thead>
                    <tbody>
                      {sheet.sharing_clients.map((sc, si) =>
                        (sc.component_totals || []).map((ct, ci) => {
                          const away = isLinkedAway(sheet.recipe_id, ct.component_name, sc.client_name, sc.key);
                          const extra = getLinkedExtra(sheet.recipe_id, ct.component_name, sc.client_name, sc.key);
                          const displayTotal = ct.total_quantity + extra;
                          return (
                            <tr key={`${si}-${ci}`} className={away ? 'skipped' : ''}>
                              <td>
                                {ct.component_name}
                                {extra > 0 && <span className="linked-badge"> LINKED</span>}
                              </td>
                              <td>{sc.client_name}</td>
                              <td className="font-bold">{sc.key}</td>
                              <td>{sc.meal_size || ''}</td>
                              <td className="text-center font-bold">{away ? '-' : formatWeight(displayTotal, ct.weight_type)}</td>
                              <td>{ct.packaging}{ct.packaging_notes ? ` (${ct.packaging_notes})` : ''}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </>
              )}

              {[...sheet.individual_clients, ...sheet.sharing_clients].some(c => c.allergy_notes) && (
                <div className="allergy">
                  <strong>ALLERGY NOTES: </strong>
                  {[...sheet.individual_clients, ...sheet.sharing_clients]
                    .filter(c => c.allergy_notes)
                    .map(c => `${c.client_name} (${c.key}): ${c.allergy_notes}`)
                    .join(' | ')
                  }
                </div>
              )}
            </div>
          );
        })}
      {/* Add-ons page */}
      {packingData.recipe_sheets.some(s => s.category === 'Add-on') && (
        <div className="page">
          <div className="header">
            <span className="title">PACKING SHEET</span>
            <span className="font-bold">{packingData.selected_days.join(' + ')}</span>
            <span>{new Date().toLocaleDateString('en-GB')}</span>
            <span>PACKED BY: ______________</span>
          </div>
          <h2>EXTRAS / ADD-ONS</h2>
          {packingData.recipe_sheets.filter(s => s.category === 'Add-on').map(sheet => (
            <div key={sheet.recipe_id}>
              <h3>{sheet.recipe_name}</h3>
              <table>
                <thead><tr><th>Client</th><th>Key</th><th>Size</th><th className="text-center">Qty</th></tr></thead>
                <tbody>
                  {[...sheet.individual_clients, ...sheet.sharing_clients].map((c) => (
                    <tr key={`${c.client_name}-${c.meal_size || 'std'}`}>
                      <td>{c.client_name}</td>
                      <td className="font-bold">{c.key}</td>
                      <td>{c.meal_size || ''}</td>
                      <td className="text-center font-bold">{c.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

PackingPrintPreview.displayName = 'PackingPrintPreview';
